'use client';

import type { ReactNode } from 'react';
import { SettingsPageHeader } from './SettingsPageHeader';

type HeadingLevel = 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

type SettingsSectionProps = {
  /** Section title, rendered at the prototype's `.sec-block-head` size. */
  title: ReactNode;
  /** Which heading element to render — pick the level the page outline needs. */
  as?: HeadingLevel;
  /** Supporting copy under the title. */
  description?: ReactNode;
  /** Rendered inline after the title, e.g. a count pill. */
  pill?: ReactNode;
  /** Right-aligned controls for the section, e.g. a create action. */
  actions?: ReactNode;
  /** Forwarded to the heading so `aria-labelledby` references keep working. */
  titleId?: string;
  /** Applied to the `<section>` element, e.g. for an in-page anchor. */
  id?: string;
  /** `aria-labelledby` target; pass together with `titleId`. */
  ariaLabelledBy?: string;
  children: ReactNode;
  className?: string;
};

/**
 * The prototype's `.sec-block`: a titled group of settings rows.
 *
 * The rows themselves carry the hairline dividers, so a section is title +
 * description + a stack of rows with nothing else around it — matching the
 * prototype's "no gray container, content sits directly on the page" v2 layout.
 *
 * Capped at the prototype's settings container (1040px), like `SettingsRowGroup`
 * — a titled section is a row stack too, and the page around it is fluid so
 * tables can reach the right edge. The cap is on the `<section>` rather than on
 * the row stack alone so the title, its description and any right-aligned
 * `actions` share one right edge with the rows: `SettingsPageHeader` lays those
 * out `justify-between`, so a cap below the header would push a section's action
 * button out to the page edge while its rows stopped 1040px short.
 *
 * `max-w-[65rem]` must never be written flush against a template interpolation —
 * Tailwind's scan of this directory is textual and would silently generate no
 * rule. See `tests/unit/Components/DS/KitUtilityExtraction.test.ts`.
 */
export const SettingsSection = ({
  title,
  as = 'h2',
  description,
  pill,
  actions,
  titleId,
  id,
  ariaLabelledBy,
  children,
  className,
}: SettingsSectionProps) => (
  <section
    id={id}
    aria-labelledby={ariaLabelledBy}
    data-lula-ds=""
    className={`flex max-w-[65rem] flex-col gap-space-4 font-sans${className ? ` ${className}` : ''}`}
  >
    <SettingsPageHeader
      as={as}
      size="section"
      title={title}
      description={description}
      pill={pill}
      actions={actions}
      titleId={titleId}
    />
    <div className="flex flex-col">{children}</div>
  </section>
);

export default SettingsSection;
